'use client';

/**
 * TimeZoneSelect (TRUS-697): the picker beside a date / time field.
 *
 * A searchable list of IANA zones with the user's own zone first, then the
 * zones they picked recently, then every zone. Each option shows its current
 * abbreviation and UTC offset. The chosen zone is what the entered wall time is
 * read in; the item stores it, and everyone else sees the time in their own
 * zone with this one beside it when it differs.
 *
 * Recent zones are a per-browser convenience (localStorage, best effort).
 */
import React, { useMemo } from 'react';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { allTimeZones, isValidTimeZone, zoneAbbreviation } from '@/utils/zonedTime';

const RECENT_KEY = 'weavein.recentTimeZones';
const RECENT_MAX = 5;

export function readRecentTimeZones(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((z): z is string => isValidTimeZone(z)).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

export function rememberTimeZone(tz: string): void {
  if (!isValidTimeZone(tz)) return;
  try {
    const next = [tz, ...readRecentTimeZones().filter((z) => z !== tz)].slice(0, RECENT_MAX);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable: no recents */
  }
}

/** "America/New_York" -> "New York (America)"; "UTC" stays. */
export function timeZoneLabel(tz: string): string {
  const parts = tz.split('/');
  if (parts.length < 2) return tz.replace(/_/g, ' ');
  const city = parts.slice(1).join(' / ').replace(/_/g, ' ');
  return `${city} (${parts[0].replace(/_/g, ' ')})`;
}

// "EDT · GMT-4" per zone per hour: building ~400 options must not construct
// ~800 Intl formatters on every open (it slowed whole dialogs under load).
const hintCache = new Map<string, string>();

function zoneHint(tz: string, when: number): string {
  const hour = Math.floor(when / 3_600_000);
  const key = `${tz}|${hour}`;
  const cached = hintCache.get(key);
  if (cached !== undefined) return cached;
  const at = hour * 3_600_000;
  const abbr = zoneAbbreviation(at, tz);
  const off = offsetLabel(tz, at);
  const hint = off && abbr !== off ? `${abbr} · ${off}` : abbr;
  if (hintCache.size > 5000) hintCache.clear();
  hintCache.set(key, hint);
  return hint;
}

let zoneListCache: string[] | null = null;
function zoneList(): string[] {
  if (!zoneListCache) zoneListCache = allTimeZones();
  return zoneListCache;
}

function offsetLabel(tz: string, now: number): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(new Date(now))
      .find((p) => p.type === 'timeZoneName')?.value;
    return part || '';
  } catch {
    return '';
  }
}

export interface TimeZoneSelectProps {
  value: string;
  onChange: (tz: string) => void;
  /** The user's own zone (default: from their preferences). Listed first. */
  userTimeZone?: string;
  /** The instant whose abbreviation to show (DST); default now. */
  at?: number;
  id?: string;
  name?: string;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function TimeZoneSelect({
  value,
  onChange,
  userTimeZone,
  at,
  id,
  name,
  disabled,
  className,
  'data-testid': testId,
}: TimeZoneSelectProps) {
  const { t } = useAppTranslation();
  const ownZone = useUserTimezone();
  const mine = userTimeZone || ownZone;
  // Hour precision is enough for an abbreviation, and keeps the list memoized.
  const when = Math.floor((at ?? Date.now()) / 3_600_000) * 3_600_000;

  const options = useMemo<SearchableSelectOption[]>(() => {
    const seen = new Set<string>();
    const out: SearchableSelectOption[] = [];
    const push = (tz: string, group: string) => {
      if (!isValidTimeZone(tz) || seen.has(tz)) return;
      seen.add(tz);
      out.push({ value: tz, label: timeZoneLabel(tz), group, hint: zoneHint(tz, when) });
    };
    push(mine, t('time.zone.yours', 'Your time zone'));
    if (value && value !== mine) push(value, t('time.zone.selected', 'Selected'));
    const recentGroup = t('time.zone.recent', 'Recent');
    readRecentTimeZones().forEach((tz) => push(tz, recentGroup));
    const allGroup = t('time.zone.all', 'All time zones');
    zoneList().forEach((tz) => push(tz, allGroup));
    return out;
    // `value` only matters for the "Selected" row; recents are read when the list is built.
  }, [mine, value, when, t]);

  return (
    <div data-testid={testId} data-value={value} className={className}>
      {name ? <input type="hidden" name={name} value={value} /> : null}
      <SearchableSelect
        id={id}
        options={options}
        value={value}
        disabled={disabled}
        onValueChange={(tz) => {
          rememberTimeZone(tz);
          onChange(tz);
        }}
        placeholder={t('time.zone.placeholder', 'Time zone')}
        searchPlaceholder={t('time.zone.search', 'Search time zones (city or region)')}
        emptyText={t('time.zone.none', 'No matching time zone')}
      />
    </div>
  );
}

export default TimeZoneSelect;
